import React, { useState, useRef, useEffect, useContext } from "react";
import styles from "./ActionReplays.module.scss";
import TinySlider from "tiny-slider-react";
import SliderControls from "./SliderButton";
import { animOnSwipe } from "../utils/animation";
import { isMobile } from "react-device-detect";
import { onPreviewChange } from "../utils/storyUIHandlers";
import ActionReplayPlaceholder from "../components/ActionReplayPlaceholder";
import { AzureMP } from "react-azure-mp";
import { Context } from "../Store";
import ReactPlayer from "react-player/lazy";
import { eventNames } from "../utils/eventData";
import { capitalize, trackEvent } from "../utils/helpers";
import ShareArrow from "./ShareArrow";

// Inner component — owns sliderRef, animOnSwipe entirely.
// Keyed by selectedPlayerId in the parent so it fully remounts on player change
// with no stale refs or mid-flight effect calls.
const ActionReplaySlider = ({ videos, isTimeline, players, handleHover, openLightbox, stateData }) => {
  const sliderRef = useRef();
  const sliderId = useRef(`slider-${Math.random().toString(36).substr(2, 9)}`).current;
  const hasTrackedScrollRef = useRef(false);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    return onPreviewChange((preview) => {
      if (!preview || preview.section !== "ActionReplay") {
        setHoveredIndex(null);
      } else {
        setHoveredIndex(preview.index);
      }
    });
  }, []);

  const settings = {
    lazyload: false,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: videos.length >= 3,
    items: 1,
    edgePadding: isTimeline ? 0 : videos.length === 1 && isMobile ? 0 : 30,
    gutter: isTimeline ? 10 : 16,
    responsive: {
      640: {
        items: videos.length >= 3 ? 1 : 1,
        edgePadding: isTimeline ? 0 : videos.length >= 3 ? 180 : 0,
        gutter: isTimeline ? 10 : 16,
      },
      1024: {
        items: videos.length >= 3 ? 1 : Math.min(videos.length, 2),
        edgePadding: isTimeline ? 0 : videos.length >= 3 ? 280 : 30,
        gutter: isTimeline ? 10 : 20,
      },
      1440: {
        items: videos.length >= 3 ? 1 : Math.min(videos.length, 2),
        edgePadding: isTimeline ? 0 : videos.length >= 3 ? 420 : 0,
        gutter: isTimeline ? 10 : 20,
      },
    },
  };

  useEffect(() => {
    const slider = sliderRef.current?.slider;
    if (!slider) return;

    animOnSwipe(slider);

    const isLooping = videos.length >= 3;

    const updatePosition = (info) => {
      if (isLooping) {
        setIsFirst(false);
        setIsLast(false);
      } else {
        setIsFirst(info.index === 0);
        setIsLast(info.index + info.items >= info.slideCount);
      }
      setCurrentIndex((info.displayIndex ?? info.index + 1) - 1);
    };

    // set initial state
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);

    // Handle clicks on cloned slides (loop mode) — clones aren't in React's fiber tree
    // tiny-slider marks clones with .tns-slide-cloned so we skip real slides
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-replay-index]");
      if (!target) return;
      const idx = parseInt(target.getAttribute("data-replay-index"), 10);
      openLightbox(idx);
    };
    container.addEventListener("click", handleCloneClick);

    return () => {
      slider.events.off("indexChanged", updatePosition);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [openLightbox, videos.length]);

  return (
    <div className={styles.sliderWrapper}>
      <div
        id={sliderId}
        className={styles.sliderContainer}
        style={
          videos.length === 1 && isMobile
            ? { padding: "0 30px" }
            : !isMobile && videos.length < 3
              ? {
                  maxWidth: videos.length === 1 ? "711px" : "1200px",
                  margin: "0 auto",
                }
              : undefined
        }
      >
        {videos.length >= 3 && (
          <SliderControls slider={sliderRef} isFirst={isFirst} isLast={isLast} compact />
        )}
        {!isMobile && videos.length >= 3 && <div className={styles.peekBlockerLeft} />}
        {!isMobile && videos.length >= 3 && <div className={styles.peekBlockerRight} />}

        <TinySlider
          settings={settings}
          ref={sliderRef}
          onIndexChanged={() => {
            if (!hasTrackedScrollRef.current) {
              hasTrackedScrollRef.current = true;
              trackEvent("Action Replays", "action_replays_scroll");
            }
          }}
        >
          {videos.map((video, index) => (
            <div key={index} style={{ position: "relative" }} className={styles.actionReplayItem}>
              <div
                data-replay-index={index}
                onClick={() => openLightbox(index)}
                onMouseEnter={isMobile ? () => false : (e) => handleHover(index, e)}
                onMouseLeave={(e) => handleHover(index, e)}
                style={{ cursor: "pointer" }}
              >
                {!video.thumbnail_url && <ActionReplayPlaceholder video={{ video }} players={{ players }} />}
                {video.thumbnail_url && (
                  <div
                    className={`${styles.thumbnailWrapper}${index === currentIndex ? ` ${styles.thumbnailActive}` : ""}`}
                  >
                    <img src={video.thumbnail_url} alt="thumbnail" />
                  </div>
                )}
              </div>

              <div
                className={`${styles.slideExtras}${videos.length >= 3 && index !== currentIndex ? ` ${styles.slideExtrasHidden}` : ""}`}
              >
                {hoveredIndex !== index && (
                  <ShareArrow
                    title={`${capitalize(players.find((p) => p.id === Number(video.players))?.name)}: ${eventNames[video.type]}`}
                    url={video.video_url}
                    text={`Check out this ${eventNames[video.type]} Action Replay from our Flight Club visit.`}
                    downloadUrl={video.video_url}
                    isVideo={true}
                    customBottom={isMobile ? "31px" : "23px"}
                    sectionName="Action Replay"
                    mediaShare={{
                      mediaType: "VIDEO",
                      mediaURI: video.asset_uri,
                      videoURL: video.video_url,
                      sectionName: "Action Replay",
                      thumbnail: video.thumbnail_url,
                      videoType: video.type,
                    }}
                    cityurl={stateData.cityurl}
                    venue={stateData.venue}
                    azureurl={stateData.azureurl}
                    videourl={stateData.videourl}
                    guid={stateData.guid}
                    country={stateData.country}
                  />
                )}

                <div className={styles.actionReplayText}>
                  <p className={styles.actioReplayTitle}>
                    {capitalize(players.find((p) => p.id === Number(video.players))?.name)}{" "}
                    {eventNames[video.type]}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </TinySlider>
      </div>
      {(videos.length >= 3 || (videos.length === 2 && isMobile)) && (
        <div className={styles.slideCounter}>
          <span>{currentIndex + 1}</span>/{videos.length}
        </div>
      )}
    </div>
  );
};

const ActionReplays = (props) => {
  const [state] = useContext(Context);
  const videos = props.videos || state.actionReplays;
  const [selectedPlayerId, setSelectedPlayerId] = useState("all");
  const players = state.players || [];

  const playersWithReplays = players
    .filter((player) => videos.some((video) => Number(video.players) === Number(player.id)))
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );

  const filteredVideos =
    selectedPlayerId === "all"
      ? videos
      : videos.filter((video) => Number(video.players) === Number(selectedPlayerId));

  const isTimeline = props.section === "timeline";

  const actionReplaysMedia = filteredVideos.map((video) => ({
    mediaType: "VIDEO",
    mediaURI: video.asset_uri,
    mediaProvider: video.video_provider,
    videoURL: video.video_url,
    sectionName: "Action Replay",
    thumbnail: video.thumbnail_url,
    videoType: video.type,
    eventName: eventNames[video.type],
  }));

  const actionReplaysVideos = filteredVideos.map((video) => {
    if (video.video_provider === "Azure") {
      return (
        <div className={styles.actionReplayItem}>
          <AzureMP
            options={{ controls: true, autoplay: true, muted: true }}
            src={[{ src: video.video_url, type: "application/vnd.ms-sstr+xml" }]}
          />
        </div>
      );
    } else if (video.video_provider === "AzureBlob") {
      return (
        <div className={styles.actionReplayItem}>
          <ReactPlayer
            className="react-player"
            url={video.video_url}
            muted
            controls
            playing
            width="100%"
            height="100%"
          />
        </div>
      );
    } else {
      return (
        <iframe
          id={"video" + video.asset_uri}
          src={`https://player.vimeo.com/video/${video.asset_uri}?title=0&portait=0&byline=0&loop=true&api=1&playsinline=true&player_id=video${video.asset_uri}`}
          frameBorder="0"
          width="1920px"
          height="1080px"
        />
      );
    }
  });

  if (state.debug) console.log(actionReplaysMedia);

  const handleHover = (index, e) => {
    props.handleHover(
      {
        section: "ActionReplay",
        index,
        ...actionReplaysMedia[index],
        target: e.currentTarget,
        timeStamp: e.timeStamp,
        groupLength: Math.min(actionReplaysMedia.length, 2),
        sources: actionReplaysVideos,
        mediashare: actionReplaysMedia,
      },
      e.type,
    );
  };

  const openLightbox = (index) => {
    props.openLightbox({
      section: "ActionReplay",
      index,
      sources: actionReplaysVideos,
      mediashare: actionReplaysMedia,
    });
  };

  return (
    <div
      id={props.section !== "timeline" ? "action-replays" : undefined}
      className={`${props.section !== "timeline" ? "section" : ""} scale-slider ${styles.actionReplaysSection}`}
    >
      {props.section !== "timeline" && (
        <>
          <h3>Moments to watch again</h3>
          <h2>Your Game Highlights</h2>
        </>
      )}
      <div className={styles.filterRow}>
        <select
          id="action-replay-player-filter"
          className={styles.filterSelect}
          value={selectedPlayerId}
          onChange={(e) => {
            trackEvent("Action Replays", "action_replay_filter", e.target.value);
            setSelectedPlayerId(e.target.value);
          }}
        >
          <option value="all">All players</option>
          {playersWithReplays.map((player) => (
            <option key={player.id} value={player.id}>
              {capitalize(player.name)}
            </option>
          ))}
        </select>
      </div>

      <ActionReplaySlider
        key={selectedPlayerId}
        videos={filteredVideos}
        isTimeline={isTimeline}
        players={players}
        handleHover={handleHover}
        openLightbox={openLightbox}
        stateData={state}
      />
    </div>
  );
};

export default ActionReplays;
